import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import YouTubePlayer from "youtube-player"

export default class extends Controller {
  static values = {
    courseUuid: String,
    lessonId: Number,
    videoIdentifier: String,
    lastPosition: Number,
    nextLessonUrl: String,
    studyMode: { type: Boolean, default: false },
    currentLessonIntroViewed: Boolean,
    startTime: { type: Number, default: 0 },
    endTime: { type: Number, default: 0 },
    segmentMode: { type: Boolean, default: true }
  }

  static targets = ["stage", "speedButton", "currentSpeed", "playPauseButton", "playIcon", "pauseIcon", "stepForwardButton", "stepBackButton", "studyPanel", "studyModeStatus", "currentSpeedDisplay"]

  initialize() {
    this.progressInterval = null
    this.endTimeInterval = null
    this.playbackSpeed = 1.0
    this.hasResumed = false
    this.isPlaying = false
    this.frameStep = 0.033 // Approximate 1 frame at 30fps
    this.playerInitialized = false
  }

  connect() {
    // Only initialize player if intro has been viewed
    if (this.currentLessonIntroViewedValue) {
      this.initializePlayer()
    }

    // Handle page unload
    this.handleWindowUnload = this.handleWindowUnload.bind(this)
    window.addEventListener('beforeunload', this.handleWindowUnload)

    // Listen for replay event from interstitial
    this.handleReplayBound = this.handleReplay.bind(this)
    this.element.addEventListener('lesson-interstitial:replay', this.handleReplayBound)

    // Listen for watch-full event from interstitial
    this.handleWatchFullBound = this.handleWatchFull.bind(this)
    this.element.addEventListener('lesson-interstitial:watchFull', this.handleWatchFullBound)
  }

  initializePlayer() {
    if (this.playerInitialized) return
    this.playerInitialized = true

    const player = YouTubePlayer(this.stageTarget, {
      width: '100%',
      height: '100%',
      playerVars: {
        autoplay: 0,
        controls: 1,
        modestbranding: 1,
        rel: 0
      }
    })

    player.on('ready', e => {
      this.youtube = e.target
    })

    player.on('stateChange', e => {
      const time = e.target.playerInfo.currentTime

      // PLAYING
      if (e.data === 1) {
        this.isPlaying = true
        this.updatePlayPauseButton()
        this.startProgressTracking()
      }

      // PAUSED
      if (e.data === 2) {
        this.isPlaying = false
        this.updatePlayPauseButton()
        this.stopProgressTracking()
        this.updateProgress(time)
      }

      // ENDED
      if (e.data === 0) {
        this.isPlaying = false
        this.updatePlayPauseButton()
        this.stopProgressTracking()
        this.updateProgress(time)

        // Show completion interstitial instead of auto-advancing
        this.showCompletionInterstitial()
      }
    })

    // Load video with smart start position respecting segment window
    const loadOptions = { videoId: this.videoIdentifierValue }
    const resumePos = this.lastPositionValue
    const segStart = this.startTimeValue || 0
    const segEnd = this.endTimeValue

    if (resumePos > segStart && (segEnd === 0 || resumePos < segEnd)) {
      loadOptions.startSeconds = resumePos
    } else if (segStart > 0) {
      loadOptions.startSeconds = segStart
    }
    player.loadVideoById(loadOptions)
  }

  disconnect() {
    this.stopProgressTracking()
    if (this.handleWindowUnload) {
      window.removeEventListener('beforeunload', this.handleWindowUnload)
    }
    if (this.handleReplayBound) {
      this.element.removeEventListener('lesson-interstitial:replay', this.handleReplayBound)
    }
    if (this.handleWatchFullBound) {
      this.element.removeEventListener('lesson-interstitial:watchFull', this.handleWatchFullBound)
    }
  }

  startProgressTracking() {
    // Update progress every 10 seconds
    if (!this.progressInterval) {
      this.progressInterval = setInterval(() => {
        if (this.youtube && this.youtube.getCurrentTime) {
          try {
            // YouTube iframe API getCurrentTime() is synchronous
            const time = this.youtube.getCurrentTime()
            if (typeof time === 'number') {
              this.updateProgress(time)
            }
          } catch (error) {
            console.warn('Error tracking progress:', error)
          }
        }
      }, 10000) // 10 seconds
    }

    // Poll for end-time boundary every 500ms
    if (!this.endTimeInterval) {
      this.endTimeInterval = setInterval(() => {
        if (this.segmentModeValue && this.endTimeValue > 0 && this.youtube && this.youtube.getCurrentTime) {
          try {
            const current = this.youtube.getCurrentTime()
            if (typeof current === 'number' && current >= this.endTimeValue) {
              this.youtube.pauseVideo()
              this.updateProgress(this.endTimeValue)
              this.showCompletionInterstitial()
            }
          } catch (error) {
            console.warn('Error checking end time:', error)
          }
        }
      }, 500)
    }
  }

  stopProgressTracking() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval)
      this.progressInterval = null
    }
    if (this.endTimeInterval) {
      clearInterval(this.endTimeInterval)
      this.endTimeInterval = null
    }
  }

  updateProgress(position) {
    const url = `/courses/${this.courseUuidValue}/lessons/${this.lessonIdValue}/update_progress`

    post(url, {
      responseKind: 'json',
      body: {
        position: position,
        watched_duration: 10
      }
    }).then(response => {
      if (response.ok) {
        response.json.then(data => {
          // Dispatch event for chapter navigation to update UI
          if (data.completed) {
            this.dispatch('lessonCompleted', { detail: { lessonId: this.lessonIdValue } })
          }

          // Update course progress bar if it exists
          this.dispatch('progressUpdated', {
            detail: {
              courseCompletion: data.course_completion_percentage
            }
          })
        })
      }
    })
  }

  handleWindowUnload() {
    // Save progress on page unload
    if (this.youtube && this.youtube.getCurrentTime) {
      try {
        // YouTube iframe API getCurrentTime() is synchronous, not a Promise
        const time = this.youtube.getCurrentTime()

        if (typeof time === 'number' && time > 0) {
          const formData = new FormData()
          formData.append('position', time)
          formData.append('watched_duration', 10)

          const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          if (csrfToken) {
            formData.append('authenticity_token', csrfToken)
          }

          const url = `/courses/${this.courseUuidValue}/lessons/${this.lessonIdValue}/update_progress`
          if (navigator.sendBeacon) {
            navigator.sendBeacon(url, formData)
          }
        }
      } catch (error) {
        // Silently fail during page unload to avoid console errors
        console.warn('Could not save progress on unload:', error)
      }
    }
  }

  // Playback speed controls
  setSpeed(speed) {
    this.playbackSpeed = speed
    if (this.youtube) {
      try {
        this.youtube.setPlaybackRate(speed)

        // Auto-mute at slow speeds (audio becomes inaudible/distorted)
        if (speed < 1.0) {
          this.youtube.mute()
        } else {
          this.youtube.unMute()
        }
      } catch (error) {
        console.error('Error setting playback rate:', error)
      }
    }
    this.updateSpeedDisplay()
  }

  speed025(event) {
    event.preventDefault()
    this.setSpeed(0.25)
  }

  speed050(event) {
    event.preventDefault()
    this.setSpeed(0.5)
  }

  speed100(event) {
    event.preventDefault()
    this.setSpeed(1.0)
  }

  updateSpeedDisplay() {
    if (this.hasCurrentSpeedTarget) {
      this.currentSpeedTarget.textContent = `${this.playbackSpeed}x`
    }

    // Update current speed display in Study Mode panel
    if (this.hasCurrentSpeedDisplayTarget) {
      this.currentSpeedDisplayTarget.textContent = `${this.playbackSpeed}x`
    }

    // Update button states
    this.speedButtonTargets.forEach(button => {
      const speed = parseFloat(button.dataset.speed)
      if (speed === this.playbackSpeed) {
        button.classList.add('bg-blue-600', 'text-white')
        button.classList.remove('bg-gray-200', 'text-gray-700')
      } else {
        button.classList.remove('bg-blue-600', 'text-white')
        button.classList.add('bg-gray-200', 'text-gray-700')
      }
    })
  }

  // Skip controls
  skipForward(event) {
    event.preventDefault()
    if (this.youtube && this.youtube.getCurrentTime) {
      try {
        const time = this.youtube.getCurrentTime()
        this.youtube.seekTo(time + 10, true)
      } catch (error) {
        console.error('Error skipping forward:', error)
      }
    }
  }

  skipBackward(event) {
    event.preventDefault()
    if (this.youtube && this.youtube.getCurrentTime) {
      try {
        const time = this.youtube.getCurrentTime()
        this.youtube.seekTo(Math.max(0, time - 10), true)
      } catch (error) {
        console.error('Error skipping backward:', error)
      }
    }
  }

  // Play/Pause control
  togglePlayPause(event) {
    event.preventDefault()
    if (!this.youtube) return

    try {
      if (this.isPlaying) {
        this.youtube.pauseVideo()
      } else {
        this.youtube.playVideo()
      }
    } catch (error) {
      console.error('Error toggling play/pause:', error)
    }
  }

  updatePlayPauseButton() {
    if (!this.hasPlayPauseButtonTarget) return

    if (this.isPlaying) {
      // Show pause icon, hide play icon on all play/pause buttons
      this.playIconTargets.forEach(icon => icon.classList.add('hidden'))
      this.pauseIconTargets.forEach(icon => icon.classList.remove('hidden'))
      // Disable frame step buttons when playing (affects all frame step buttons)
      if (this.hasStepForwardButtonTarget) {
        this.stepForwardButtonTargets.forEach(btn => btn.disabled = true)
      }
      if (this.hasStepBackButtonTarget) {
        this.stepBackButtonTargets.forEach(btn => btn.disabled = true)
      }
    } else {
      // Show play icon, hide pause icon on all play/pause buttons
      this.playIconTargets.forEach(icon => icon.classList.remove('hidden'))
      this.pauseIconTargets.forEach(icon => icon.classList.add('hidden'))
      // Enable frame step buttons when paused (affects all frame step buttons)
      if (this.hasStepForwardButtonTarget) {
        this.stepForwardButtonTargets.forEach(btn => btn.disabled = false)
      }
      if (this.hasStepBackButtonTarget) {
        this.stepBackButtonTargets.forEach(btn => btn.disabled = false)
      }
    }
  }

  // Frame stepping (only works when paused)
  stepForward(event) {
    event.preventDefault()
    if (!this.youtube || this.isPlaying) return

    try {
      const time = this.youtube.getCurrentTime()
      this.youtube.seekTo(time + this.frameStep, true)
    } catch (error) {
      console.error('Error stepping forward:', error)
    }
  }

  stepBackward(event) {
    event.preventDefault()
    if (!this.youtube || this.isPlaying) return

    try {
      const time = this.youtube.getCurrentTime()
      this.youtube.seekTo(Math.max(0, time - this.frameStep), true)
    } catch (error) {
      console.error('Error stepping backward:', error)
    }
  }

  // Study Mode toggle
  toggleStudyMode(event) {
    event.preventDefault()
    this.studyModeValue = !this.studyModeValue
    this.updateStudyModeDisplay()
  }

  updateStudyModeDisplay() {
    const isStudyMode = this.studyModeValue

    // Slide panel up/down
    if (this.hasStudyPanelTarget) {
      if (isStudyMode) {
        this.studyPanelTarget.style.maxHeight = this.studyPanelTarget.scrollHeight + 'px'
        // Remove overflow-hidden after animation completes to allow popover to show
        setTimeout(() => {
          if (this.studyModeValue) { // Check again in case it was toggled quickly
            this.studyPanelTarget.style.overflow = 'visible'
          }
        }, 300) // Match transition duration
      } else {
        // Add overflow-hidden back for collapse animation
        this.studyPanelTarget.style.overflow = 'hidden'
        this.studyPanelTarget.style.maxHeight = '0'
      }
    }

    // Update status text with neon glow
    if (this.hasStudyModeStatusTarget) {
      this.studyModeStatusTarget.textContent = isStudyMode ? 'On' : 'Off'
      // Both On and Off states have purple glow
      this.studyModeStatusTarget.style.textShadow = '0 0 10px rgba(168, 85, 247, 0.5), 0 0 20px rgba(168, 85, 247, 0.3)'
    }
  }

  showCompletionInterstitial() {
    const interstitialModal = document.querySelector('[data-controller="lesson-interstitial"]')
    if (interstitialModal) {
      // Get the Stimulus controller instance and call show() method
      const stimulus = window.Stimulus || this.application
      const interstitialController = stimulus.getControllerForElementAndIdentifier(
        interstitialModal,
        'lesson-interstitial'
      )

      if (interstitialController) {
        interstitialController.show()
      } else {
        // Fallback if controller not found
        interstitialModal.classList.remove('hidden')
        document.body.style.overflow = 'hidden'
      }
    }
  }

  handleReplay(event) {
    if (this.youtube) {
      this.youtube.seekTo(this.startTimeValue || 0, true)
      this.youtube.playVideo()
      this.isPlaying = true
      this.updatePlayPauseButton()
    }
  }

  handleWatchFull(event) {
    if (this.youtube) {
      this.segmentModeValue = false // disable end-time enforcement
      this.youtube.seekTo(0, true)
      this.youtube.playVideo()
      this.isPlaying = true
      this.updatePlayPauseButton()
    }
  }

  get player() {
    return this.youtube
  }
}
